'use client'

import { useEffect } from 'react'
import * as CookieConsent from 'vanilla-cookieconsent'
import {
  consentToGoogle,
  trackingMode,
  type TrackingConfig,
  type TrackingMode,
} from '@/lib/consent-tracking'

// The site template can remount on navigation. Scripts and vendor listeners outlive React mounts.
let loadedMode: TrackingMode = 'none'
let defaultsQueued = false
let reloading = false

// Trackers set their cookies on the registrable domain (e.g. .creatortv.hu), which the
// CMP's default (the current hostname) misses, so erase on every parent domain too.
function eraseTrackerCookies(pattern: RegExp) {
  const labels = window.location.hostname.split('.')
  for (let i = 0; i < labels.length - 1; i++) {
    CookieConsent.eraseCookies(pattern, '/', labels.slice(i).join('.'))
  }
}

type TrackingWindow = Window & {
  dataLayer?: unknown[]
  gtag?: (...args: unknown[]) => void
}

export default function ConsentTracking({ config }: { config: TrackingConfig }) {
  useEffect(() => {
    if (!config.enabled) return
    const trackingWindow = window as TrackingWindow
    const dataLayer = (trackingWindow.dataLayer ??= [])
    // Google's command queue requires an Arguments object, rather than a plain event object.
    const gtag = (trackingWindow.gtag ??= function (..._args: unknown[]) {
      dataLayer.push(arguments)
    })
    if (!defaultsQueued) {
      gtag('consent', 'default', consentToGoogle({ analytics: false, advertisement: false }))
      defaultsQueued = true
    }

    const syncConsent = () => {
      if (reloading) return
      const valid = CookieConsent.validConsent()
      const consent = {
        analytics: valid && CookieConsent.acceptedCategory('analytics'),
        advertisement: valid && CookieConsent.acceptedCategory('advertisement'),
      }
      const nextMode = trackingMode(config, consent)
      if (loadedMode !== 'none' && nextMode !== loadedMode) {
        // Removing a script cannot stop its timers/listeners. Reload after the CMP saves cc_cookie.
        reloading = true
        if (!consent.analytics) {
          Object.assign(trackingWindow, { [`ga-disable-${config.gaId}`]: true })
          eraseTrackerCookies(/^_ga(?:_|$)/)
        }
        if (!consent.advertisement) {
          eraseTrackerCookies(/^(_fbp|_fbc|_gcl_.*|_ttp|_?tt_enable_cookie|ttcsid(_.*)?)$/)
        }
        gtag('consent', 'update', consentToGoogle(consent))
        window.location.reload()
        return
      }
      gtag('consent', 'update', consentToGoogle(consent))
      if (nextMode === 'none' || loadedMode !== 'none') return
      loadedMode = nextMode
      const script = document.createElement('script')
      script.async = true
      // Support nonce-based CSP without introducing an inline bootstrap script.
      const nonce = document.querySelector<HTMLScriptElement>('script[nonce]')?.nonce
      if (nonce) script.nonce = nonce
      if (nextMode === 'container') {
        dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' })
        script.src = `https://www.googletagmanager.com/gtm.js?id=${config.gtmId}`
      } else {
        // Analytics-only visits never execute the mixed GTM container or its custom HTML pixels.
        gtag('set', 'ads_data_redaction', true)
        gtag('set', 'allow_google_signals', false)
        gtag('set', 'allow_ad_personalization_signals', false)
        gtag('js', new Date())
        gtag('config', config.gaId, {
          allow_google_signals: false,
          allow_ad_personalization_signals: false,
        })
        script.src = `https://www.googletagmanager.com/gtag/js?id=${config.gaId}`
      }
      document.head.appendChild(script)
    }

    // onConsent covers saved preferences on startup; onChange runs after changed preferences save.
    window.addEventListener('cc:onConsent', syncConsent)
    window.addEventListener('cc:onChange', syncConsent)
    syncConsent() // Also covers a CMP that finished initialising before this effect mounted.
    return () => {
      window.removeEventListener('cc:onConsent', syncConsent)
      window.removeEventListener('cc:onChange', syncConsent)
    }
  }, [config])

  return null
}
